import React from "react";
import { FC } from "react";
import { NavLink } from "react-router-dom";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";

export interface CommonLayoutProps {
  children?: React.ReactNode;
}

const CommonLayout: FC<CommonLayoutProps> = ({ children }) => {
  const { currentUser, loading } = useSelector(
    (state: RootState) => state.user
  );

  // Navigation items configuration
  const navigationItems = [
    {
      to: "/account",
      label: "Account Info",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      to: "/account-password",
      label: "Password",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
    },
    {
      to: "/favorite-listings",
      label: "Favorites",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
    },
  ];

  // Bookings tab — students only, never shown to hosts.
  // Spliced in after Password so ordering stays Account Info, Password, Bookings, Favorites.
  if (!currentUser?.is_host) {
    navigationItems.splice(2, 0, {
      to: "/account-bookings",
      label: "Bookings",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    });
  }

  // Add host-specific navigation item conditionally
  if (currentUser && currentUser.host_verified) {
    navigationItems.push({
      to: "/add-listing-1",
      label: "List Property",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      ),
    });
  }

  return (
    <div className="nc-CommonLayoutProps bg-neutral-50 dark:bg-neutral-900 min-h-screen">
      {/* Navigation Header */}
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 shadow-sm">
        <div className="container">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 overflow-x-auto scrollbar-hide">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group relative flex items-center gap-2 px-4 lg:px-6 py-5 lg:py-6 text-sm lg:text-base font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? "text-primary-500 dark:text-primary-500"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`transition-colors ${
                        isActive
                          ? "text-primary-500 dark:text-primary-500"
                          : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                    {/* Active indicator */}
                    <span
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary-500 dark:bg-primary-500 transition-transform duration-200 ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Mobile Navigation - Horizontal Scroll */}
          <nav className="md:hidden flex items-center gap-2 overflow-x-auto scrollbar-hide px-4 py-3">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 border ${
                    isActive
                      ? "bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-500 border-primary-200 dark:border-primary-500/30 shadow-sm"
                      : "bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-neutral-600"
                  }`
                }
              >
                <span className="text-current">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Page Content */}
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="py-8 sm:py-12 lg:py-16">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CommonLayout;